import { useEffect, useMemo, useRef } from 'react';
import {
    buildSongPlaybackWindows,
    findActiveSongWindow,
    getSongPlaybackOffsetSeconds
} from './slideshowAudioTimeline';

/**
 * How far the audio element may drift from the playhead before we snap it back. Below this we
 * let the audio clock run freely so normal playback stays smooth; a larger gap means the user
 * seeked/scrubbed (or resumed), so we realign.
 */
const SYNC_THRESHOLD_SECONDS = 0.25;

interface IUseSlideshowAudioParams {
    /** Sound-timeline segments in display order. */
    segments: SpApi.ISlideshowSongSegment[];
    /** Whether the slideshow is currently playing. */
    isPlaying: boolean;
    /** Current playhead position from the slideshow ticker (ms). */
    currentTimeMs: number;
    /** Playback volume (0–1). */
    volume: number;
    /**
     * Resolves a signed, playable stream URL for a song id. Only called for segments that don't
     * already carry an embedded `streamUrl` (see ISlideshowSongSegment) — the client-facing
     * slideshow payload embeds one per song, so this fallback is Studio-editor-only in practice.
     * Callers own how (and whether) this is authenticated. Returning null (or never resolving)
     * simply means that song stays silent.
     */
    getStreamUrl: (songId: string) => Promise<string | null>;
    /**
     * Called whenever this hook starts or stops claiming exclusive control of audio playback —
     * not on every frame, only on the true/false transition. True whenever playing with at least
     * one song segment configured, *regardless* of whether the playhead is currently within a
     * specific song's window (a gap between songs still counts, so another source stays ducked
     * for the whole slideshow, not just individual song windows). Also called with `false` on
     * unmount if it was last reported active. Callers use this to duck/mute other audio sources
     * (e.g. gallery background music) while a slideshow's own music plays — and, symmetrically,
     * to pause the slideshow itself when a competing audio source takes over instead.
     */
    onActiveChange?: (isActive: boolean) => void;
}

/**
 * Plays a slideshow's sound-timeline songs in sync with its preview/playback position.
 *
 * Songs are concatenated sequentially from t=0 (see `buildSongPlaybackWindows`) and driven by a
 * single HTMLAudioElement — sequential playback means only one song sounds at a time. Sync is
 * "loose": the audio clock runs on its own between frames and is only corrected when it drifts
 * past `SYNC_THRESHOLD_SECONDS` (a seek/scrub/resume) or when the playhead crosses into a
 * different song (swap the source). This avoids per-frame `currentTime` writes, which would
 * stutter the audio.
 */
export function useSlideshowAudio({
    segments,
    isPlaying,
    currentTimeMs,
    volume,
    getStreamUrl,
    onActiveChange
}: IUseSlideshowAudioParams): void {
    const windows = useMemo(() => buildSongPlaybackWindows(segments), [segments]);

    const audioRef = useRef<HTMLAudioElement | null>(null);
    const streamUrlCacheRef = useRef<Map<string, string>>(new Map());
    // The song we've asked to load vs. the song whose src is actually applied. They differ
    // briefly while a signed stream URL is being fetched.
    const requestedSongIdRef = useRef<string | null>(null);
    const loadedSongIdRef = useRef<string | null>(null);
    const loadTokenRef = useRef(0);
    // Latest values for async continuations that resolve a frame or more later.
    const currentTimeMsRef = useRef(currentTimeMs);
    const windowsRef = useRef(windows);
    const getStreamUrlRef = useRef(getStreamUrl);
    const onActiveChangeRef = useRef(onActiveChange);
    const isActiveRef = useRef(false);

    currentTimeMsRef.current = currentTimeMs;
    windowsRef.current = windows;
    getStreamUrlRef.current = getStreamUrl;
    onActiveChangeRef.current = onActiveChange;

    // Own a single audio element for the lifetime of the hook.
    useEffect(() => {
        const audio = new Audio();
        audioRef.current = audio;

        return () => {
            audio.pause();
            audio.src = '';
            audioRef.current = null;

            if (isActiveRef.current) {
                isActiveRef.current = false;
                onActiveChangeRef.current?.(false);
            }
        };
    }, []);

    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.volume = volume;
        }
    }, [volume]);

    // Warm the stream-URL cache for every song while playing so crossing a song boundary
    // doesn't stall on a fetch.
    useEffect(() => {
        if (!isPlaying) {
            return;
        }

        let cancelled = false;

        windows.forEach((window) => {
            if (window.streamUrl || streamUrlCacheRef.current.has(window.songId)) {
                return;
            }

            void getStreamUrlRef.current(window.songId).then((url) => {
                if (!cancelled && url) {
                    streamUrlCacheRef.current.set(window.songId, url);
                }
            });
        });

        return () => {
            cancelled = true;
        };
    }, [isPlaying, windows]);

    // Keep the audio element aligned with the playhead. Runs each frame while playing (the
    // parent already re-renders per frame for the playhead) but only issues audio commands when
    // something actually needs to change.
    useEffect(() => {
        const audio = audioRef.current;

        if (!audio) {
            return;
        }

        const active = findActiveSongWindow(windows, currentTimeMs);
        // Claims exclusivity whenever playing with any song segments at all — not just while the
        // playhead sits inside a specific song's window — so another audio source stays ducked
        // across gaps between songs too.
        const isActive = isPlaying && windows.length > 0;

        if (isActive !== isActiveRef.current) {
            isActiveRef.current = isActive;
            onActiveChangeRef.current?.(isActive);
        }

        if (!isPlaying || !active) {
            if (!audio.paused) {
                audio.pause();
            }

            // If a stream URL is still being fetched, cancel it so the pending load can't call
            // play() after we've paused. Bumping the token makes the in-flight start() bail, and
            // clearing the requested id makes resume re-request the song. A fully-loaded song is
            // left intact so normal resume stays gapless.
            if (loadedSongIdRef.current !== requestedSongIdRef.current) {
                loadTokenRef.current += 1;
                requestedSongIdRef.current = null;
            }

            return;
        }

        // Crossed into a different song (or first play): load and start it.
        if (requestedSongIdRef.current !== active.songId) {
            // Silence the previous song right away — its window no longer covers the playhead
            // (boundary crossed, or its segment was removed from the timeline) — so it can't
            // keep sounding while the next song's stream URL loads.
            if (!audio.paused) {
                audio.pause();
            }

            requestedSongIdRef.current = active.songId;
            const token = ++loadTokenRef.current;

            const start = (url: string | null) => {
                const currentAudio = audioRef.current;

                if (!url || !currentAudio || token !== loadTokenRef.current) {
                    return;
                }

                // The playhead may have advanced (or moved to another song) while the URL was
                // being fetched — re-resolve against the latest position before committing.
                const latestActive = findActiveSongWindow(
                    windowsRef.current,
                    currentTimeMsRef.current
                );

                if (!latestActive || latestActive.songId !== active.songId) {
                    return;
                }

                currentAudio.src = url;
                loadedSongIdRef.current = active.songId;
                currentAudio.currentTime = getSongPlaybackOffsetSeconds(
                    latestActive,
                    currentTimeMsRef.current
                );
                void currentAudio.play().catch(() => {
                    /* autoplay policy or decode error */
                });
            };

            const cached = active.streamUrl ?? streamUrlCacheRef.current.get(active.songId);

            if (cached) {
                start(cached);
            } else {
                void getStreamUrlRef.current(active.songId).then((url) => {
                    if (url) {
                        streamUrlCacheRef.current.set(active.songId, url);
                    }

                    start(url);
                });
            }

            return;
        }

        // Same song still loading — the async start() above will begin playback.
        if (loadedSongIdRef.current !== active.songId) {
            return;
        }

        // Same song playing: realign only on a meaningful drift (seek/scrub/resume).
        const desiredOffsetSeconds = getSongPlaybackOffsetSeconds(active, currentTimeMs);

        if (Math.abs(audio.currentTime - desiredOffsetSeconds) > SYNC_THRESHOLD_SECONDS) {
            audio.currentTime = desiredOffsetSeconds;
        }

        if (audio.paused) {
            void audio.play().catch(() => {
                /* autoplay policy or decode error */
            });
        }
    }, [windows, isPlaying, currentTimeMs, volume]);
}
